import { Component, OnInit, inject } from "@angular/core";
import { ResourceViewModel } from "../../view-models/ResourceViewModel";
import { AnlageViewModel } from "../../view-models";
import {
  FormGroup,
  FormBuilder,
  FormControl,
  AbstractControl,
  Validators,
} from "@angular/forms";
import {
  AnlagenClientProvider,
  AppointmentArrangement,
  TaskInsertDto,
  TaskStatus,
} from "src/app/web-api/web-api";
import { CstDialogsService } from "src/app/common-components/dialogs/cst-dialogs.service";
import { CstDialogResult } from "src/app/common-components/dialogs/cst-dialog-enums";
import { UserService } from "src/app/services/user.service";
import {
  CstDateTimeService,
  TimeSpan,
} from "src/app/services/cst-date-time.service";
import {
  DropdownType,
  IFormDropDownItem,
} from "src/app/common-components/form-controls/form-dropdown/dropdown.component";
import { NEVER, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { BFF_IsSaasInstance } from "src/app/injection-tokens/backend-feature-flags";
import { CstValidatorCalendarWeek } from "src/app/helpers/CstValidators";

@Component({
  selector: "app-task-insert",
  templateUrl: "./task-insert.component.html",
  styleUrls: ["./task-insert.component.scss"],
  standalone: false,
})
export class TaskInsertComponent
  extends CstResponsivePopupComponentBase<any, TaskInsertDto>
  implements OnInit
{
  private _fb = inject(FormBuilder);
  private _anlageClient = inject(AnlagenClientProvider);
  private _msg = inject(CstDialogsService);
  private _userService = inject(UserService);
  private _dts = inject(CstDateTimeService);
  private _dialog = inject(CstDialogsService);
  isSaasInstance = inject(BFF_IsSaasInstance);

  //extends DialogContentBase
  public dropDownType = DropdownType;

  form: FormGroup = null;
  months: IFormDropDownItem[] = [];
  calendarWeekRules = { W: /[0-5]/ };
  resources: Array<ResourceViewModel> = [];

  validationMessageCalendarWeekPattern = $localize`:@@PlanungAPI_TaskInsert_CalendarWeek_ValidationMessage:Must be in format CW/YYYY, eg. 19/2025, 43/2026, 51/2026`;
  validationMessageCalendarWeekInvalid = $localize`:@@PlanungAPI_TaskInsert_CalendarWeek_InvalidWeek:Week must be between 1 and 54`;

  public get fc(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }
  public get fcDateRangesActive(): FormControl {
    return this.fc.dateRangesActive as FormControl;
  }
  public get fcDateRanges(): any {
    return (this.fc.dateRanges as FormGroup).controls;
  }
  public get fcDrPeriodOfAction(): any {
    return this.fcDateRanges.periodOfAction.controls;
  }
  public get fcDrPlannedWeek(): any {
    return this.fcDateRanges.plannedWeek.controls;
  }
  public get fcWorkLocation(): any {
    return (this.fc.workLocation as FormGroup).controls;
  }

  public get areDateRangesActive(): boolean {
    return this.fcDateRangesActive.value;
  }

  public set anlageGroup(value: string) {
    this.fc.anlageGroup.setValue(value);
  }
  public get anlageGroup() {
    return this.fc.anlageGroup.value;
  }

  public get DueDate() {
    return this.fc.dueDate.value as Date;
  }

  async ngOnInit() {
    this.months = Array.from({ length: 13 }, (_v, mon) => {
      if (mon === 0) {
        return { value: 0, label: "-- kein --" };
      }
      const date = new Date();
      date.setDate(1);
      date.setMonth(mon - 1); // TS Date wertet Monate bei 0 beginnend aus
      return {
        value: mon,
        label: date.toLocaleString(undefined, { month: "long" }),
      };
    });

    const fcDateRangesActive = new FormControl(false);
    const fcYearPeriodMonth = new FormControl(0);
    const fcRawCalendarWeek = new FormControl("", [
      Validators.required,
      CstValidatorCalendarWeek,
    ]);
    const fcToDoDate = new FormControl(null);

    const minDate = new Date(0);
    this.form = this._fb.group({
      categoryID: new FormControl(null, Validators.required),
      label: new FormControl("", [
        Validators.required,
        Validators.maxLength(4096),
      ]),
      description: new FormControl("", Validators.maxLength(131_072)),
      assignedToResourceID: null,
      state: TaskStatus.Preparation,
      anlageNrIntern: "",
      anlageGroup: "",
      appointmentArrangement: AppointmentArrangement.None,
      dateRangesActive: fcDateRangesActive,
      dateRanges: this._fb.group({
        periodOfAction: this._fb.group({
          month: fcYearPeriodMonth,
          year: new Date().getFullYear(),
        }),
        plannedWeek: this._fb.group({
          week: null,
          year: new Date().getFullYear(),
        }),
        todoDate: fcToDoDate,
        todoTime: null,
        rawCalendarWeek: fcRawCalendarWeek,
      }),
      workTimePlanned: new Date(minDate.getFullYear(), 0, 1, 0, 0, 0, 0),
      workLocation: this._fb.group({
        name: "",
        street: "",
        streetNo: "",
        city: "",
        postalCode: "",
        driveTimePlanned: new Date(minDate.getFullYear(), 0, 1, 0, 0, 0, 0),
        kilometresPlanned: 0.0,
      }),
      dueDate: [null],
      sendPushNotification: false,
      pushNotificationTag: "",
    });

    fcDateRangesActive.valueChanges.subscribe((v) => {
      if (v === true) {
        fcToDoDate.setValidators(Validators.required);
        fcYearPeriodMonth.setValidators(Validators.required);
        fcRawCalendarWeek.setValidators(Validators.required);
        fcYearPeriodMonth.setValidators(Validators.required);
      } else {
        fcToDoDate.setValidators(null);
        fcRawCalendarWeek.setValidators(null);
        fcYearPeriodMonth.setValidators(null);
      }
      fcToDoDate.updateValueAndValidity();
      fcRawCalendarWeek.updateValueAndValidity();
      fcYearPeriodMonth.updateValueAndValidity();
    });
  }

  public OnYearPeriodInputChange(isFirstCall: boolean) {
    if (isFirstCall) {
      const d = this._dts.GetDateFromYearPeriod(
        this.fcDateRanges.periodOfAction.value,
      );

      this.fcDateRanges.todoDate.setValue(d);
      this.OnTodoDateInputChange();
    }
  }
  public OnCalendarWeekInputChange(isFirstCall: boolean) {
    if (!this.fcDateRanges.rawCalendarWeek.valid) {
      return;
    }

    const value: string = this.fcDateRanges.rawCalendarWeek.value;
    const result = this._dts.GetCalendarWeekFromString(value);

    if (result === null) {
      this.fcDrPlannedWeek.week.setValue(null);
      this.fcDrPlannedWeek.year.setValue(null);
      return;
    }
    if (!result.success) {
      this.fcDateRanges.rawCalendarWeek.setErrors({ incorrect: true });
      this.fcDrPlannedWeek.week.setValue(null);
      this.fcDrPlannedWeek.year.setValue(null);
      return;
    }
    if (value !== result.correctedString) {
      this.fcDateRanges.rawCalendarWeek.setValue(result.correctedString);
    }
    if (this.fcDateRangesActive.untouched) {
      this.fcDateRangesActive.setValue(true);
    }
    this.fcDrPlannedWeek.week.setValue(result.calendarWeek.week);
    this.fcDrPlannedWeek.year.setValue(result.calendarWeek.year);
    if (isFirstCall) {
      const date = this._dts.GetDateFromCalendarWeek(result.calendarWeek);
      this.fcDateRanges.todoDate.setValue(date);
    }
    this.fcDrPeriodOfAction.month.setValue(
      this.fcDateRanges.todoDate.value.getMonth() + 1,
    );
    this.fcDrPeriodOfAction.year.setValue(
      this.fcDateRanges.todoDate.value.getFullYear(),
    );
  }

  public OnTodoDateInputChange() {
    const value = this.fcDateRanges.todoDate.value;
    const cw = this._dts.GetCalendarWeekFromDate(value);

    if (cw === null) {
      this.fcDateRanges.rawCalendarWeek.setValue(null);
    } else {
      this.fcDateRanges.rawCalendarWeek.setValue(
        `${cw.week.toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        })}/${cw.year.toLocaleString(undefined, {
          minimumIntegerDigits: 4,
          useGrouping: false,
        })}`,
      );
    }
    this.OnCalendarWeekInputChange(false);
  }

  public onFacilitySelected(result: AnlageViewModel) {
    if (!result) {
      return;
    }

    const obs = result.anlagenstandort
      ? of(result.anlagenstandort)
      : this._anlageClient.getStandort(undefined, result.anlageNrIntern);
    obs
      .pipe(
        switchMap((ort) => {
          let fields = $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_Location:Task Location:\n`;
          if (ort.name) {
            fields += $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_Name:Name: ${ort.name}\n`;
          }
          if (ort.street) {
            fields += $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_Street:Street: ${ort.street}\n`;
          }
          if (ort.streetNo) {
            fields += $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_StreetNo:Street No.: ${ort.streetNo}\n`;
          }
          if (ort.city) {
            fields += $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_City:City: ${ort.city}\n`;
          }
          if (ort.postalCode) {
            fields += $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_PostalCode:Postal Code: ${ort.postalCode}\n`;
          }
          if (result.anlageGroup) {
            fields += $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt_FacilityGroup:Facility Group:\n${result.anlageGroup}`;
          }
          return this._msg
            .AskYesNo(
              $localize`:@@PlanungAPI_TaskInsert_FacilityInformationAdopt:Should the facility information be adopted?\n\n` +
                fields,
            )
            .pipe(map((res) => (res === CstDialogResult.Yes ? ort : null)));
        }),
      )
      .subscribe((ort) => {
        if (!ort) {
          return;
        }
        this.fcWorkLocation.name.setValue(ort.name);
        this.fcWorkLocation.street.setValue(ort.street);
        this.fcWorkLocation.streetNo.setValue(ort.streetNo);
        this.fcWorkLocation.city.setValue(ort.city);
        this.fcWorkLocation.postalCode.setValue(ort.postalCode);
        if (result.anlageGroup) {
          this.fc.anlageGroup.setValue(result.anlageGroup);
        }
        if (result.appointmentArrangement) {
          this.form.patchValue({
            appointmentArrangement: result.appointmentArrangement,
          });
        }
      });
  }

  public CanSendNotification() {
    return (
      this._userService.CurrentUser.settings.system &&
      this._userService.CurrentUser.settings.system.azureNotificationHub &&
      this._userService.CurrentUser.settings.system.azureNotificationHub
        .isLicensed
    );
  }

  public OnAccept() {
    if (!this.form.valid) {
      return;
    }
    const fval = this.form.value;
    const taskInsertDto = new TaskInsertDto();
    taskInsertDto.init(fval);
    if (
      taskInsertDto.dateRanges.plannedWeek &&
      !taskInsertDto.dateRanges.plannedWeek.week
    ) {
      taskInsertDto.dateRanges.plannedWeek = null;
    }

    of(taskInsertDto)
      .pipe(
        switchMap((model) => {
          const todo = this._dts.DateToInteger(
            this.fcDateRanges.todoDate.value,
            false,
          );
          const due = this._dts.DateToInteger(this.DueDate, true);
          if (todo > due) {
            const opts = {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            } as Intl.DateTimeFormatOptions;
            const dueDate = this.DueDate?.toLocaleString(undefined, opts);
            const todoDate = this.fcDateRanges.todoDate.value?.toLocaleString(
              undefined,
              opts,
            );
            return this._dialog
              .AskYesCancel(
                $localize`:@@TaskDetail_Ask_SetDueDateToTodoDate:The todo date \"${todoDate}\" is after the due date \"${dueDate}\".\nShould the due date be set to the todo date?\nOtherwise the due date must be adjusted.`,
              )
              .pipe(
                switchMap((res) => {
                  if (res !== CstDialogResult.Yes) {
                    return NEVER;
                  }
                  model.dueDate = model.dateRanges.todoDate;
                  return of(model);
                }),
              );
          }
          return of(model);
        }),
      )
      .subscribe((model) => {
        if (model.dateRanges.todoDate) {
          // Korrektur für unterschiedliche Time-Zones.
          // Sonst würde die Zeit "00:00:00" GMT als IsoString "23:00:00" vom vorherigen Tag ergeben
          // und es wird der falsche Termin gespeichert.
          const orig = new Date(model.dateRanges.todoDate);
          model.dateRanges.todoDate.setUTCHours(0, 0, 0, 0);
          model.dateRanges.todoDate.setUTCFullYear(
            orig.getFullYear(),
            orig.getMonth(),
            orig.getDate(),
          );
        }

        model.dateRanges.todoTime = this._GetCorrectTodoTime(
          model.dateRanges.todoDate,
          model.dateRanges.todoTime,
        );

        if (fval.workTimePlanned instanceof Date) {
          model.workTimePlanned = TimeSpan.fromDate(
            fval.workTimePlanned,
          ).toJSON();
        } else if (typeof fval.workTimePlanned === "string") {
          model.workTimePlanned = TimeSpan.fromString(
            fval.workTimePlanned,
          ).toJSON();
        }

        if (fval.workLocation.driveTimePlanned instanceof Date) {
          model.workLocation.driveTimePlanned = TimeSpan.fromDate(
            fval.workLocation.driveTimePlanned,
          ).toJSON();
        } else if (typeof fval.workLocation.driveTimePlanned === "string") {
          model.workLocation.driveTimePlanned = TimeSpan.fromString(
            fval.workLocation.driveTimePlanned,
          ).toJSON();
        }

        if (model.assignedToResourceID === " ") {
          model.assignedToResourceID = null;
        }

        this.close(model);
      });
  }
  public OnCancel() {
    this.close(null);
  }

  private _GetCorrectTodoTime(todoDate: Date, todoTime: Date) {
    let finalTodoTime: Date;
    if (!todoTime) {
      finalTodoTime = null;
    } else if (!todoDate) {
      finalTodoTime = todoTime;
    } else {
      const d = todoDate as Date;
      const t = todoTime as Date;
      finalTodoTime = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        t.getHours(),
        t.getMinutes(),
        t.getSeconds(),
        t.getMilliseconds(),
      );
    }
    return finalTodoTime;
  }
}
